import { useMemo, useState } from 'react';
import { adminApi } from '../../api/admin';
import { useAsync, Banner, Item, Empty, Advanced } from './ui';
import { Modal } from '../../components/Modal';
import { pmsGroupLabel, groupBy } from '../../lib/pmsGroup';

type Row = Record<string, unknown>;
const blank = {
  equipmentId: '', name: '', location: '', templateId: 'HT_LT_TRANSFORMER', active: true,
  category: 'daily' as 'daily' | 'pms',
  targetIntervalMin: '', minIntervalMin: '', dueGraceMin: '', overdueAfterMin: '',
  manufacturer: '', pmsNumber: '', uniqueId: '', yearOfInstallation: '', pmsCycleDay: '',
  serialNumber: '', capacity: '',
};
const isYes = (v: unknown) => v === true || String(v).toLowerCase() === 'true';

export default function EquipmentAdmin() {
  const { data, error, loading, reload } = useAsync(() => adminApi.listEquipment(), [], 'equipment');
  const templates = useAsync(() => adminApi.listTemplates(), [], 'templates');
  const [form, setForm] = useState<typeof blank | null>(null);
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState<'daily' | 'pms'>('daily');

  const isExisting = !!form && !!data?.rows.find((r) => String(r['Equipment ID']) === form.equipmentId);
  const tplTitleById = useMemo(() => {
    const m = new Map<string, string>();
    templates.data?.templates.forEach((t) => m.set(t.templateId, t.title));
    return m;
  }, [templates.data]);

  function edit(r: Row) {
    setForm({
      equipmentId: String(r['Equipment ID'] ?? ''),
      name: String(r['Equipment Name'] ?? ''),
      location: String(r['Location'] ?? ''),
      templateId: String(r['Template ID'] ?? 'HT_LT_TRANSFORMER'),
      active: isYes(r['Active']),
      category: String(r['Category'] ?? 'daily') === 'pms' ? 'pms' : 'daily',
      targetIntervalMin: String(r['Target Interval (min)'] ?? ''),
      minIntervalMin: String(r['Minimum Interval (min)'] ?? ''),
      dueGraceMin: String(r['Due Grace (min)'] ?? ''),
      overdueAfterMin: String(r['Overdue After (min)'] ?? ''),
      manufacturer: String(r['Manufacturer'] ?? ''),
      pmsNumber: String(r['PMS Number'] ?? ''),
      uniqueId: String(r['Unique ID'] ?? ''),
      yearOfInstallation: String(r['Year of Installation'] ?? ''),
      pmsCycleDay: String(r['PMS Cycle Day'] ?? ''),
      serialNumber: String(r['Serial Number'] ?? ''),
      capacity: String(r['Capacity'] ?? ''),
    });
    setMsg('');
  }

  async function save() {
    if (!form) return;
    setSaving(true); setMsg('');
    try { await adminApi.saveEquipment(form); setMsg('Saved.'); setForm(null); reload(); }
    catch (e) { setMsg(e instanceof Error ? e.message : 'Save failed'); }
    finally { setSaving(false); }
  }

  return (
    <div>
      <div className="spread">
        <h1>Equipment</h1>
        <button className="sm" onClick={() => { setForm({ ...blank }); setMsg(''); }}>+ Add</button>
      </div>
      <Banner error={error} ok={msg === 'Saved.' ? msg : undefined} />
      {msg && msg !== 'Saved.' && <p className="error">{msg}</p>}
      {loading && <div className="spinner" />}
      {data && data.rows.length === 0 && <Empty icon="🔌">No equipment yet. Add one to get started.</Empty>}

      {data && data.rows.length > 0 && (() => {
        const rowCategory = (r: Row) => (String(r['Category'] ?? 'daily') === 'pms' ? 'pms' : 'daily');
        const dailyRows = data.rows.filter((r) => rowCategory(r) === 'daily');
        const pmsRows = data.rows.filter((r) => rowCategory(r) === 'pms');
        const shownRows = view === 'daily' ? dailyRows : pmsRows;
        const pmsGroups = groupBy(pmsRows, (r) => pmsGroupLabel(tplTitleById.get(String(r['Template ID'])) ?? String(r['Template ID'])) || 'Other');
        const row = (r: Row, i: number) => (
          <Item
            key={i}
            stripe={isYes(r['Active']) ? 'ok' : 'bad'}
            title={String(r['Equipment Name'] ?? r['Equipment ID'])}
            meta={<>
              <span>{String(r['Location'] ?? '')}</span>
              <span>{String(r['Equipment ID'] ?? '')}</span>
              <span>{String(r['Template ID'] ?? '')}</span>
              {view === 'pms' && (
                r['PMS Cycle Day'] ? <span>Day {String(r['PMS Cycle Day'])}</span>
                  : <span style={{ color: 'var(--warn)' }}>unscheduled</span>
              )}
              {!isYes(r['Active']) && <span style={{ color: 'var(--bad)' }}>inactive</span>}
            </>}
            actions={<button className="secondary xs" onClick={() => edit(r)}>Edit</button>}
          />
        );
        return (
          <>
            <div className="choice-group" role="tablist" aria-label="Equipment view" style={{ marginBottom: 12 }}>
              <button type="button" className={`choice-btn ${view === 'daily' ? 'on' : ''}`} role="tab" aria-selected={view === 'daily'} onClick={() => setView('daily')}>
                Daily readings ({dailyRows.length})
              </button>
              <button type="button" className={`choice-btn ${view === 'pms' ? 'on' : ''}`} role="tab" aria-selected={view === 'pms'} onClick={() => setView('pms')}>
                PMS checks ({pmsRows.length})
              </button>
            </div>
            {shownRows.length === 0 && <Empty icon="🔌">{`No ${view === 'daily' ? 'daily-reading' : 'PMS'} equipment yet.`}</Empty>}
            {view === 'daily' && <div className="cardlist">{dailyRows.map(row)}</div>}
            {view === 'pms' && pmsGroups.map((g) => (
              <div key={g.label} style={{ marginBottom: 4 }}>
                <h3 className="pmsGroup-head">{g.label} <span className="muted">({g.items.length})</span></h3>
                <div className="cardlist">{g.items.map(row)}</div>
              </div>
            ))}
          </>
        );
      })()}

      {form && (
        <Modal
          title={isExisting ? 'Edit equipment' : 'New equipment'}
          onClose={() => !saving && setForm(null)}
          footer={<>
            <button className="secondary" onClick={() => setForm(null)} disabled={saving}>Cancel</button>
            <button onClick={save} disabled={saving || !form.equipmentId || !form.name}>{saving ? 'Saving…' : 'Save'}</button>
          </>}
        >
          <label>Equipment ID</label>
          <input value={form.equipmentId} disabled={isExisting}
            onChange={(e) => setForm({ ...form, equipmentId: e.target.value.toUpperCase() })} placeholder="BB-BMT-002" />
          <label>Name</label>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <label>Location</label>
          <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          <label>Template</label>
          <select value={form.templateId} onChange={(e) => setForm({ ...form, templateId: e.target.value })}>
            {(templates.data?.templates ?? [{ templateId: 'HT_LT_TRANSFORMER', title: 'HT & LT Transformer Reading' }]).map((t) => (
              <option key={t.templateId} value={t.templateId}>{t.title} ({t.templateId})</option>
            ))}
          </select>
          <label>Shows up in</label>
          <div className="choice-group">
            <button type="button" className={`choice-btn ${form.category === 'daily' ? 'on' : ''}`}
              onClick={() => setForm({ ...form, category: 'daily' })}>Daily readings</button>
            <button type="button" className={`choice-btn ${form.category === 'pms' ? 'on' : ''}`}
              onClick={() => setForm({ ...form, category: 'pms' })}>PMS checks</button>
          </div>

          {form.category === 'pms' && (
            <>
              <label>PMS rotation day</label>
              <input inputMode="numeric" value={form.pmsCycleDay} placeholder="e.g. 6 (of a 15-day cycle)"
                onChange={(e) => setForm({ ...form, pmsCycleDay: e.target.value })} />
              <p className="muted tiny" style={{ margin: '4px 0 0' }}>
                Which day of the rotating PMS calendar this item is inspected on. Operators only see it in their
                "PMS today" queue on this day (set the cycle length/start date in Settings). Leave blank until scheduled —
                unscheduled items still show up every day so nothing gets missed.
              </p>
            </>
          )}

          <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 14 }}>
            <input type="checkbox" style={{ width: 'auto', minHeight: 0 }} checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active
          </label>

          <p className="muted tiny" style={{ marginTop: 14 }}>Nameplate identity — shown on the PMS report letterhead.</p>
          <div className="grid3">
            <div><label>Manufacturer</label><input value={form.manufacturer} placeholder="Power Switchgears"
              onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} /></div>
            <div><label>PMS / asset number</label><input value={form.pmsNumber} placeholder="HT-A1/3-2"
              onChange={(e) => setForm({ ...form, pmsNumber: e.target.value })} /></div>
            <div><label>Year of installation</label><input value={form.yearOfInstallation} placeholder="2010"
              onChange={(e) => setForm({ ...form, yearOfInstallation: e.target.value })} /></div>
          </div>
          <div className="grid3">
            <div><label>Serial number</label><input value={form.serialNumber} placeholder="25755424"
              onChange={(e) => setForm({ ...form, serialNumber: e.target.value })} /></div>
            <div><label>Capacity</label><input value={form.capacity} placeholder="500 KVA"
              onChange={(e) => setForm({ ...form, capacity: e.target.value })} /></div>
          </div>
          <label>Unique ID</label>
          <input value={form.uniqueId} placeholder="UNM/HT Panel/Block A/2019/B1/A1"
            onChange={(e) => setForm({ ...form, uniqueId: e.target.value })} />

          <Advanced>
            <p className="muted tiny">
              Leave blank to use the global schedule from Settings (120 / 60 / 15 / 150 min). Set all
              four for equipment on a different cadence — e.g. a monthly PMS checklist: target 43200
              (30 days), minimum 1440 (1 day), due grace 4320 (3 days), overdue after 46800 (~32 days).
            </p>
            <div className="grid3">
              <div><label>Target (min)</label><input inputMode="numeric" value={form.targetIntervalMin} placeholder="120"
                onChange={(e) => setForm({ ...form, targetIntervalMin: e.target.value })} /></div>
              <div><label>Minimum (min)</label><input inputMode="numeric" value={form.minIntervalMin} placeholder="60"
                onChange={(e) => setForm({ ...form, minIntervalMin: e.target.value })} /></div>
              <div><label>Due grace (min)</label><input inputMode="numeric" value={form.dueGraceMin} placeholder="15"
                onChange={(e) => setForm({ ...form, dueGraceMin: e.target.value })} /></div>
              <div><label>Overdue after (min)</label><input inputMode="numeric" value={form.overdueAfterMin} placeholder="150"
                onChange={(e) => setForm({ ...form, overdueAfterMin: e.target.value })} /></div>
            </div>
          </Advanced>
        </Modal>
      )}
    </div>
  );
}
